import { Component, ElementRef, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FilterStoreService } from '../store/filter-store.service';
import { GlobalService } from 'src/app/_services/global.service';
import cjson from 'compressed-json';

interface CriteriaPair {
  key: string;
  value: string;
    chip?: string | null;

}

/**
 * @title Drag&Drop custom placeholder
 */
@Component({
  selector: 'app-hiring-criteria',
  imports: [CdkDropList, CdkDrag, CdkDragPlaceholder, ReactiveFormsModule, CommonModule],
  templateUrl: './hiring-criteria.component.html',
  styleUrl: './hiring-criteria.component.scss',
  standalone: true
})
export class HiringCriteriaComponent {

  @Input() firstTime: boolean = false;

  constructor(public router: Router, private modalService: NgbModal, private fb: FormBuilder, private filterStore: FilterStoreService, private route: ActivatedRoute) {
      this.searchType = this.route.snapshot.queryParams['type'];

    this.criteriaForm = this.fb.group({
      criterias: this.fb.array([])
    });
    this.initCriteria();
  }

  // fixed number of criteria inputs — this count never changes at runtime
  readonly totalCriteria = 5;
  // how many of the fixed inputs get pre-filled with AI suggestions on load
  readonly autoFillCount = 2;

  @ViewChildren('criteriaInput') criteriaInputs!: QueryList<ElementRef<HTMLInputElement>>;

  showHeading = true; // matches your existing @if (!showHeading) block

  // full pool from your API / suggestion service
  private allSuggestions: string[] = this.filterStore.screeningCriteria();

  // whatever didn't make it into the form initially
  aiSuggestedCriterias: string[] = [];

  criteriaForm: FormGroup;
  searchType: string;

  // snapshot of criteria values as loaded, used to detect real changes
  private initialCriteriaValues: string[] = [];


  get criterias(): FormArray {
    return this.criteriaForm.get('criterias') as FormArray;
  }

  // true when at least one of the fixed inputs is currently empty
  get hasEmptySlot(): boolean {
    return this.criterias.controls.some(c => !c.value?.trim());
  }

  private initCriteria(): void {
    // pull previously saved criteria from the store (adjust the accessor to match your service)
    const savedCriterias: string[] = this.filterStore.getFilters()?.screeningCriteria ?? [];

    // the AI suggestion pool stays as-is, sourced from screeningCriteria()
    const suggestionPool: string[] = this.allSuggestions?.length ? [...(this.allSuggestions)] : [];

    // always create exactly `totalCriteria` inputs — the count is fixed and never grows/shrinks
    if (savedCriterias.length > 0) {
      for (let i = 0; i < this.totalCriteria; i++) {
        const value = savedCriterias[i] ?? '';
        this.criterias.push(this.fb.control(value));
      }

      // remove any patched criteria that also exist in the suggestion pool
      this.aiSuggestedCriterias = suggestionPool.filter(s => !savedCriterias.includes(s));
    } else {
      // no saved criteria — auto-fill the first `autoFillCount` inputs with AI suggestions,
      // the remaining inputs stay empty (but still visible)
      const autoFilled = suggestionPool.slice(0, this.autoFillCount);

      for (let i = 0; i < this.totalCriteria; i++) {
        const value = autoFilled[i] ?? '';
        this.criterias.push(this.fb.control(value));
      }

      this.aiSuggestedCriterias = suggestionPool.slice(this.autoFillCount);
    }

    // snapshot after all inputs are populated, so later edits can be diffed against this
    this.initialCriteriaValues = this.criterias.value.map((v: string) => v ?? '');
  }

  // true only once the current values differ from what was loaded (typed edits,
  // suggestion fills, removals, or drag-reorder all count as a change)
  get hasChanges(): boolean {
    const current: string[] = this.criterias.value.map((v: string) => v ?? '');
    if (current.length !== this.initialCriteriaValues.length) return true;
    return current.some((v, i) => v !== this.initialCriteriaValues[i]);
  }

  addFromSuggestion(suggestionIndex: number): void {
    const value = this.aiSuggestedCriterias[suggestionIndex];
    if (!value) return;

    // only ever fill an existing empty input — inputs are never added
    const emptyControl = this.criterias.controls.find(c => !c.value?.trim());
    if (!emptyControl) return; // all 5 inputs are already filled

    emptyControl.setValue(value);
    this.aiSuggestedCriterias.splice(suggestionIndex, 1);
  }

  // clears the text for that slot, then shifts every value after it up by
  // one so there's never a gap between filled inputs — e.g. clearing
  // criteria 2 while criteria 3 has a value moves criteria 3's value into
  // slot 2. The inputs themselves never move or disappear, only the values.
  removeCriteria(index: number): void {
    const controls = this.criterias.controls;
    if (!controls[index]) return;

    const removedValue = controls[index].value;

    const values = controls.map(c => c.value);
    values.splice(index, 1); // drop the cleared slot
    values.push('');         // keep the array at totalCriteria length

    values.forEach((value, i) => controls[i].setValue(value));

    if (removedValue?.trim()) {
      this.aiSuggestedCriterias.push(removedValue);
    }
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.criterias.controls, event.previousIndex, event.currentIndex);
    this.criterias.updateValueAndValidity();
  }

  goToProjectList(): void {
    // your existing navigation logic
  }

  // Enter no longer adds a new row — there is nothing to do here since the
  // input count is fixed, kept as a no-op hook in case you want to add
  // behavior later (e.g. move focus to the next input).
  onEnter(index: number): void {}

  get visibleSuggestions(): string[] {
    return this.aiSuggestedCriterias.slice(0, 5);
  }

  cancel() {
    this.modalService.dismissAll();
  }

  exploreCandidates() {
    if (!this.hasChanges) {
      this.modalService.dismissAll();
      return;
    }

const criteria = (this.criterias.value ?? [])
  .filter((c: string) => c?.trim());   // drops '', null, undefined, whitespace-only

if (criteria.length) {
  const criteriaMap = this.buildCriteriaMap(criteria);
  this.filterStore.criteriaMap.set(criteriaMap);
  this.filterStore.setFilter({ screeningCriteria: criteria });
} else {
  this.filterStore.criteriaMap.set({});          // or null / new Map(), depending on your type
  this.filterStore.setFilter({ screeningCriteria: [] });
}
    this.modalService.dismissAll();
    const filterData = this.filterStore.getFilters();
    const localKey = GlobalService.hash(JSON.stringify(cjson.compress(filterData)));
    localStorage.setItem(localKey, JSON.stringify(cjson.compress(filterData)));
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        type: this.searchType,
        key: encodeURIComponent(localKey)
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });

  }

  private buildCriteriaMap(criterias: string[]): CriteriaPair[] {
  return criterias
    .filter((c: string) => c?.trim())
    .map((value, index) => ({
      key: `c${index + 1}`,
      value
    }));
}
}
